import React, { useCallback, useEffect } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import ReactPaginate from 'react-paginate';
import { RootState } from 'MyTypes';
import ListItem from './ListItem';
import * as listActions from '../../listActions';
import * as listSelectors from '../../listSelectors';
import * as filterActions from '../../../Filter/filterActions';
import styles from './List.module.scss';
import AZList from '../AZ/AZList';
import * as appConfigSelectors from '../../../AppConfig/appConfigSelectors';
import { HydratedCurriculumType } from '../../../Common/types/AppConfigTypes';
import * as filterSelectors from '../../../Filter/filterSelectors';
import NoItemsTile from '../../../Common/components/NoItemsTile/NoItemsTile';
import Loading from '../../../Common/components/Loading/Loading';

export interface ListProps {
    activeCurriculumType: HydratedCurriculumType;
}

type Props = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps> &
    ListProps;

const mapStateToProps = (state: RootState, props: ListProps) => ({
    list: listSelectors.mappedListSelector(state, props),
    listIsLoading: listSelectors.listIsLoadingSelector(state),
    isSearchResults: listSelectors.isSearchResultListSelector(state),
    searchResultsTerm: listSelectors.searchResultsTermSelector(state),
    activeSearchTerm: filterSelectors.searchTermSelector(state),
    startsWith: filterSelectors.startsWithSelector(state),
    dynamicFilters: filterSelectors.dynamicFiltersSelector(state),
    activePageIndex: filterSelectors.activePageIndexSelector(state),
    totalPages: listSelectors.totalPagesSelector(state),
    activeCTSlug: listSelectors.activeCTSlugSelector(state),
    shouldRenderAZComponent: appConfigSelectors.shouldRenderAZSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch) =>
    bindActionCreators(
        {
            getAZList: listActions.getAZList,
            getSearchList: listActions.getSearchList,
            clearSearchResults: listActions.clearSearchResults,
            updateSearchTerm: filterActions.updateSearchTerm,
            setActivePage: filterActions.setActivePage,
        },
        dispatch,
    );

const List: React.FC<Props> = ({
    getAZList,
    getSearchList,
    activeCurriculumType,
    startsWith,
    searchResultsTerm,
    isSearchResults,
    activeSearchTerm,
    clearSearchResults,
    updateSearchTerm,
    listIsLoading,
    list,
    setActivePage,
    activePageIndex,
    totalPages,
    activeCTSlug,
    shouldRenderAZComponent,
    dynamicFilters,
}) => {
    const handlePagination = ({ selected }: { selected: number }) => {
        if (selected !== activePageIndex) {
            setActivePage(selected);
        }
    };

    const handleClearSearchTerm = useCallback(() => {
        clearSearchResults();
        updateSearchTerm('');
    }, [clearSearchResults, updateSearchTerm]);

    useEffect(() => {
        if (activeSearchTerm) {
            getSearchList();
        } else {
            getAZList();
        }
    }, [
        startsWith,
        searchResultsTerm,
        activeCTSlug,
        activePageIndex,
        getAZList,
        getSearchList,
    ]);

    return (
        <div className={styles.listContainer}>
            {shouldRenderAZComponent &&
                Object.keys(dynamicFilters).length === 0 && <AZList />}
            <div className={styles.listItemsContainer}>
                {listIsLoading || !activeCurriculumType ? (
                    <Loading />
                ) : (
                    <div>
                        {isSearchResults ? (
                            <div className={styles.searchResultsContainer}>
                                <h2 className={styles.searchResultsHeading}>
                                    Search Results for... &quot;
                                    {searchResultsTerm}&quot;
                                </h2>
                                <button
                                    className={styles.clearSearchButton}
                                    onClick={handleClearSearchTerm}
                                >
                                    Clear Search
                                </button>
                            </div>
                        ) : null}
                        {list && list.length ? (
                            list.map(item => (
                                <ListItem key={item.uuid} listItem={item} />
                            ))
                        ) : (
                            <NoItemsTile />
                        )}
                        {totalPages > 1 ? (
                            <div id={'list-pagination'}>
                                <ReactPaginate
                                    previousLabel={'Prev'}
                                    nextLabel={'Next'}
                                    breakLabel={'...'}
                                    pageCount={totalPages}
                                    initialPage={activePageIndex}
                                    marginPagesDisplayed={0}
                                    pageRangeDisplayed={2}
                                    onPageChange={handlePagination}
                                    containerClassName={styles.pagination}
                                    activeClassName={styles.pagination__active}
                                    disabledClassName={
                                        styles.pagination__disabled
                                    }
                                    breakClassName={styles.pagination__break}
                                />
                            </div>
                        ) : null}
                    </div>
                )}
            </div>
        </div>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(List);
